/**
 * MapModal — a location map inside the action-button modal shell (XL by default).
 */

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { ActionButtonModal } from '../results/ActionButtonModal';
import type { ActionSpec } from '../../config/actionButtons';

const LocationMap = lazy(() => import('./LocationMap'));

interface Props {
  spec: Extract<ActionSpec, { type: 'map' }>;
  estimateGuid?: string | null;
  retailerId?: number | null;
  onClose: () => void;
}

export const MapModal: React.FC<Props> = ({ spec, estimateGuid, retailerId, onClose }) => {
  const { t } = useTranslation('results');
  // Fill the modal: viewport height minus the dialog margins, title bar, search box and
  // paddings (~230px). The list column matches the map's height, so the body is exactly one
  // screen tall and nothing inside needs the dialog's own scrollbar.
  const fit = () => Math.max(320, Math.min(1100, window.innerHeight - 230));
  const [height, setHeight] = useState(fit);
  useEffect(() => {
    const onResize = () => setHeight(fit());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <ActionButtonModal title={spec.title || t('map.modalTitle')} contentHtml="" size={spec.size ?? 'xl'} headerBg={spec.headerBg} headerFg={spec.headerFg} onClose={onClose} bodyClassName="p-0" staticBackdrop>
      <Suspense fallback={<div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}><Loader2 size={22} className="animate-spin" /></div>}>
        <LocationMap mapId={spec.mapId} context="modal" estimateGuid={estimateGuid ?? null} retailerId={retailerId ?? null} heightPx={height} />
      </Suspense>
    </ActionButtonModal>
  );
};

export default MapModal;
